import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import { TaxonomyType } from '@revelio/data-access';

import { environment } from '../../../environments/environment';
import type { TreeSelection } from '../../../next/CustomDash/filters/types/filters';
import { useRoleTreeSelectionLists } from '../../selection-lists/hooks/useRoleTreeSelectionLists';
import { useTaxonomyTree } from '../../selection-lists/hooks/useTaxonomyTree';
import type {
  AllSelectionListsWithSkillTaxonomyKey,
  RoleSelectionListKey,
} from '../../selection-lists/types';
import { flattenTreeToCompoundIdItems } from '../../selection-lists/utils/flattenTreeToCompoundIdItems';
import type { CompanyMapperSearchResult } from '../../types/company-result-item';

export const MIN_SEARCH_LENGTH = 2;

export type EntitySearchOption = {
  label: string;
  value: string;
  data: TreeSelection;
};

export type EntitySearchSelectConfig = {
  options: EntitySearchOption[];
  isLoading: boolean;
  onInputChange?: (value: string) => void;
  searchTerm?: string;
  searchResultsCount?: number;
};

const useCompanyTypeahead = (searchTerm: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['company-search', searchTerm],
    queryFn: async ({ signal }) => {
      const response = await fetch(`${environment.COMPANY_MAPPER_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: searchTerm, employee_cnt_min: 1 }),
        signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json() as Promise<CompanyMapperSearchResult[]>;
    },
    enabled: searchTerm.trim().length >= MIN_SEARCH_LENGTH,
    staleTime: 30_000,
  });

  const rawMappedResults = useMemo(
    () =>
      (data ?? [])
        .sort((a, b) => (b.raw_emp_count || 0) - (a.raw_emp_count || 0))
        .map((result) => ({
          id: `${result.primary_identifiers?.rics_k10}_${result.primary_identifiers?.rics_k400}_${result.company_id}`,
          name: result.primary_identifiers?.primary_name ?? result.primary_name,
        })),
    [data]
  );

  return {
    rawMappedResults,
    isLoading: isLoading && searchTerm.trim().length >= MIN_SEARCH_LENGTH,
  };
};

export const treeSelectionToEntitySearchOption = (
  selection: TreeSelection
): EntitySearchOption => ({
  label: selection.name,
  value: selection.id,
  data: selection,
});

const ensureSelectionsPresent = (
  options: EntitySearchOption[],
  selections: TreeSelection[]
): EntitySearchOption[] => {
  const optionMap = new Map(options.map((option) => [option.value, option]));
  selections.forEach((selection) => {
    if (!optionMap.has(selection.id)) {
      optionMap.set(selection.id, treeSelectionToEntitySearchOption(selection));
    }
  });
  return Array.from(optionMap.values());
};

export const useCompanyEntitySearchConfig = (
  storedSelections: TreeSelection[]
): EntitySearchSelectConfig => {
  const [searchTerm, setSearchTerm] = useState('');
  const initialData = storedSelections;
  const baseOptions = initialData.map(treeSelectionToEntitySearchOption);

  const { treeData, levels, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: 1,
    levels: [1, 3],
  });
  const { rawMappedResults, isLoading: typeaheadLoading } =
    useCompanyTypeahead(searchTerm);

  const industryOptions = useMemo(() => {
    if (fetching || treeData.length === 0) return [];

    const flattened = flattenTreeToCompoundIdItems(treeData, levels);

    const k10Items = flattened.rics_k10 ?? [];
    const k400Items = flattened.rics_k400 ?? [];

    return [
      ...k10Items.map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'rics_k10' as AllSelectionListsWithSkillTaxonomyKey,
        })
      ),
      ...k400Items.map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'rics_k400' as AllSelectionListsWithSkillTaxonomyKey,
        })
      ),
    ];
  }, [fetching, treeData, levels]);

  const searchResults = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    const companyOptions = rawMappedResults.map((item) =>
      treeSelectionToEntitySearchOption({
        id: item.id,
        name: item.name,
        levelId: 'company',
      })
    );

    const filteredIndustryOptions = industryOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearchTerm)
    );

    return [...companyOptions, ...filteredIndustryOptions];
  }, [rawMappedResults, searchTerm, industryOptions]);

  const options = useMemo(() => {
    const pool =
      searchTerm.trim().length >= MIN_SEARCH_LENGTH
        ? searchResults
        : baseOptions;
    return ensureSelectionsPresent(pool, storedSelections);
  }, [searchResults, baseOptions, searchTerm, storedSelections]);

  const handleInputChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  return {
    options,
    isLoading: typeaheadLoading,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: searchResults.length,
  };
};

type GeographyConfigOptions = {
  showSuggestionsWhenEmpty?: boolean;
};

export const useGeographyEntitySearchConfig = (
  storedSelections: TreeSelection[],
  { showSuggestionsWhenEmpty = true }: GeographyConfigOptions = {}
): EntitySearchSelectConfig => {
  const { treeData, levels, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Geography,
    taxonomyId: 1,
    levels: [1, 2, 3, 4],
  });
  const [searchTerm, setSearchTerm] = useState('');

  const flattenedGeo = useMemo(
    () => flattenTreeToCompoundIdItems(treeData, levels),
    [treeData, levels]
  );

  const allOptions = useMemo(() => {
    const regionItems = flattenedGeo.region ?? [];
    const countryItems = flattenedGeo.country ?? [];
    // The geo tree tags the metro level `metro_area` (legacy `msa`).
    const msaItems = flattenedGeo.metro_area ?? flattenedGeo.msa ?? [];

    return [
      ...regionItems.map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'region' as AllSelectionListsWithSkillTaxonomyKey,
        })
      ),
      ...countryItems.map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'country' as AllSelectionListsWithSkillTaxonomyKey,
        })
      ),
      ...msaItems.map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'msa' as AllSelectionListsWithSkillTaxonomyKey,
        })
      ),
    ];
  }, [flattenedGeo]);

  const cityOptions = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) return [];

    const lower = searchTerm.toLowerCase();
    // The geo tree tags the city level `city_core` (legacy `city`). `city_core`
    // isn't in the canonical key union, so read it through a string-keyed view.
    const cityItems =
      (flattenedGeo as Record<string, typeof flattenedGeo.city>).city_core ??
      flattenedGeo.city ??
      [];

    return cityItems
      .filter((item) => item.shortName.toLowerCase().includes(lower))
      .map((item) =>
        treeSelectionToEntitySearchOption({
          id: item.id,
          name: item.shortName,
          levelId: 'city' as AllSelectionListsWithSkillTaxonomyKey,
        })
      )
      .slice(0, 10);
  }, [searchTerm, flattenedGeo]);

  const filteredOptions = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      return showSuggestionsWhenEmpty ? allOptions.slice(0, 50) : [];
    }

    const lower = searchTerm.toLowerCase();
    const baseFiltered = allOptions
      .filter((opt) => opt.label.toLowerCase().includes(lower))
      .slice(0, 10);

    return [...baseFiltered, ...cityOptions];
  }, [searchTerm, allOptions, cityOptions, showSuggestionsWhenEmpty]);

  const options = useMemo(
    () => ensureSelectionsPresent(filteredOptions, storedSelections),
    [filteredOptions, storedSelections]
  );

  const handleInputChange = useCallback(
    (val: string) => setSearchTerm(val),
    []
  );

  return {
    options,
    isLoading: fetching,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: filteredOptions.length,
  };
};

type RoleConfigOptions = {
  showSuggestionsWhenEmpty?: boolean;
};

// Entity search only renders the role_k150 level as searchable options.
const ROLE_ENTITY_SEARCH_KEYS: RoleSelectionListKey[] = ['role_k150'];

export const useRoleEntitySearchConfig = (
  storedSelections: TreeSelection[],
  roleTaxonomySelection: CustomRoleTaxonomySelection,
  { showSuggestionsWhenEmpty = true }: RoleConfigOptions = {}
): EntitySearchSelectConfig => {
  const roleSelectionLists = useRoleTreeSelectionLists({
    requiredKeys: ROLE_ENTITY_SEARCH_KEYS,
    taxonomySelection: roleTaxonomySelection,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const allOptions = useMemo(() => {
    const roles = roleSelectionLists?.role_k150 ?? [];
    if (!Array.isArray(roles)) return [] as EntitySearchOption[];

    return roles
      .map((role) =>
        treeSelectionToEntitySearchOption({
          id: role.id ?? '',
          name: role.longName || role.shortName || '',
          levelId: 'role_k150',
        })
      )
      .filter((option) => option.value.length > 0);
  }, [roleSelectionLists]);

  const filteredOptions = useMemo(() => {
    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      return showSuggestionsWhenEmpty ? allOptions.slice(0, 50) : [];
    }

    const lower = searchTerm.toLowerCase();
    return allOptions
      .filter((opt) => opt.label.toLowerCase().includes(lower))
      .slice(0, 25);
  }, [searchTerm, allOptions, showSuggestionsWhenEmpty]);

  const options = useMemo(
    () => ensureSelectionsPresent(filteredOptions, storedSelections),
    [filteredOptions, storedSelections]
  );

  const handleInputChange = useCallback(
    (val: string) => setSearchTerm(val),
    []
  );

  return {
    options,
    isLoading: !roleSelectionLists,
    onInputChange: handleInputChange,
    searchTerm,
    searchResultsCount: filteredOptions.length,
  };
};
